/**
 * EmailPreview — a read-only render of the templated email the prospect WILL receive when the operator
 * confirms a review transition. It mirrors, on the client, the four server EmailTemplate codes the
 * onboardingRequests.service queues on commit (onboarding_demande_recue / _en_revue / _approuvee /
 * _rejetee). There is NO separate send endpoint — the transition IS the send — so this is a courtesy preview
 * so the operator sees the outgoing message before acting (the "email-template preview/send" affordance).
 *
 * Token-driven (no raw hex), AA-contrast, RTL-safe. The recipient address + the subject + the body all read
 * as static text; the body preserves the template's line breaks (white-space: pre-wrap). The numeric-ish
 * fields (email, reference) ride an LTR island so they read identically in FR/AR.
 */
import { useT } from "@/app";

/** Which transition's template to preview. Keyed to the funnel target statut. */
export type PreviewKind = "recue" | "en_revue" | "approuvee" | "rejetee";

export interface EmailPreviewProps {
  kind: PreviewKind;
  to: string;
  /** Interpolation vars — the subset each template reads (reference/nomLabo/wilaya/raisonRejet). */
  vars: { reference: string; nomLabo: string; wilaya?: string; raisonRejet?: string };
}

export function EmailPreview({ kind, to, vars }: EmailPreviewProps) {
  const { t } = useT();
  const subject = t(`mgmt.reqs.mail.${kind}.subject`, vars);
  const body = t(`mgmt.reqs.mail.${kind}.body`, vars);

  return (
    <section
      aria-label={t("mgmt.reqs.mail.preview")}
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--nv-s-3)",
        border: "1px solid var(--nv-border-subtle, var(--nv-line-faint))",
        borderRadius: "var(--nv-r-md, var(--nv-r-sm))",
        padding: "var(--nv-s-3)",
        background: "var(--nv-surface-2)",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--nv-s-2)" }}>
        <span aria-hidden style={{ color: "var(--nv-text-2, var(--nv-slate))" }}>
          ✉
        </span>
        <h4
          style={{
            margin: 0,
            fontSize: "var(--nv-fs-label)",
            fontWeight: 600,
            color: "var(--nv-text-2, var(--nv-slate))",
          }}
        >
          {t("mgmt.reqs.mail.preview")}
        </h4>
      </div>

      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "var(--nv-s-1) var(--nv-s-3)", margin: 0 }}>
        <dt style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
          {t("mgmt.reqs.mail.to")}
        </dt>
        <dd style={{ margin: 0, fontSize: "var(--nv-fs-caption)" }}>
          <span className="nv-ltr-island">{to}</span>
        </dd>
        <dt style={{ fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
          {t("mgmt.reqs.mail.subject")}
        </dt>
        <dd style={{ margin: 0, fontSize: "var(--nv-fs-caption)", fontWeight: 600 }}>{subject}</dd>
      </dl>

      <p
        style={{
          margin: 0,
          whiteSpace: "pre-wrap",
          fontSize: "var(--nv-fs-caption)",
          color: "var(--nv-text-1, var(--nv-ink))",
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>

      <p style={{ margin: 0, fontSize: "var(--nv-fs-caption)", color: "var(--nv-text-3, var(--nv-steel))" }}>
        {t("mgmt.reqs.mail.hint")}
      </p>
    </section>
  );
}
